import jsPDF from 'jspdf';

const GOLD       = '#C9A441';
const GOLD_LIGHT = '#F5EAC4';
const INK        = '#1A1A1A';
const GREY_DARK  = '#4A4A4A';
const GREY_MID   = '#7A7A7A';
const GREY_LIGHT = '#F7F4EE';
const WHITE      = '#FFFFFF';
const ROW_ALT    = '#FDFAF3';

const PW = 210;
const PH = 297;
const M  = 14;

function inr(n: number) {
  return `Rs. ${n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export type InvoicePdfData = {
  businessName: string;
  tagline?: string;
  address?: string;
  phone?: string;
  email?: string;
  gstNumber?: string;
  invoiceNumber: string;
  date: Date;
  customerName: string;
  customerPhone: string;
  items: Array<{ itemName: string; sku?: string; quantity: number; price: number; total: number }>;
  subtotal: number;
  tax?: number;
  discount: number;
  total: number;
  paymentMethod?: string;
};

export function generateInvoicePdfClient(data: InvoicePdfData): void {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  let y = 0;

  // ── Top gold strip ──────────────────────────────────────────────────────
  doc.setFillColor(GOLD);
  doc.rect(0, 0, PW, 3, 'F');
  y = 3;

  // ── Dark header ─────────────────────────────────────────────────────────
  doc.setFillColor(INK);
  doc.rect(0, y, PW, 28, 'F');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.setTextColor(WHITE);
  doc.text(data.businessName, M, y + 10);

  if (data.tagline) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(GOLD_LIGHT);
    doc.text(data.tagline, M, y + 16);
  }

  // TAX INVOICE badge
  doc.setFillColor(GOLD);
  doc.roundedRect(PW - M - 38, y + 5, 38, 10, 2, 2, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(INK);
  doc.text('TAX INVOICE', PW - M - 19, y + 11.5, { align: 'center' });

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.setTextColor(GOLD_LIGHT);
  doc.text(data.invoiceNumber, PW - M, y + 19, { align: 'right' });
  doc.text(data.date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }), PW - M, y + 23, { align: 'right' });

  y += 28;

  // ── FROM / BILL TO ───────────────────────────────────────────────────────
  doc.setFillColor(GREY_LIGHT);
  doc.rect(0, y, PW, 30, 'F');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(6.5);
  doc.setTextColor(GREY_MID);
  doc.text('FROM', M, y + 7);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(INK);
  doc.text(data.businessName, M, y + 13);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.setTextColor(GREY_DARK);
  let fromY = y + 18;
  if (data.address) { doc.text(data.address, M, fromY); fromY += 4.5; }
  if (data.phone)   { doc.text(`Ph: ${data.phone}`, M, fromY); fromY += 4.5; }
  if (data.gstNumber) { doc.text(`GSTIN: ${data.gstNumber}`, M, fromY); }

  doc.setDrawColor(GOLD);
  doc.setLineWidth(0.3);
  doc.line(PW / 2, y + 4, PW / 2, y + 26);

  const bx = PW / 2 + 6;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(6.5);
  doc.setTextColor(GREY_MID);
  doc.text('BILL TO', bx, y + 7);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(INK);
  doc.text(data.customerName || '—', bx, y + 13);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.setTextColor(GREY_DARK);
  if (data.customerPhone) doc.text(`Ph: ${data.customerPhone}`, bx, y + 18);

  y += 30;

  // ── Table header ─────────────────────────────────────────────────────────
  const ROW_H = 7;
  const TH_H  = 8;
  const C = {
    sr:    M,
    item:  M + 8,
    sku:   M + 80,
    qty:   M + 112,
    price: M + 128,
    total: M + 155,
  };

  doc.setFillColor(GOLD);
  doc.rect(0, y, PW, TH_H, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7);
  doc.setTextColor(INK);
  doc.text('#',          C.sr + 2,  y + 5.5);
  doc.text('ITEM',       C.item,    y + 5.5);
  doc.text('SKU',        C.sku,     y + 5.5);
  doc.text('QTY',        C.qty + 8, y + 5.5, { align: 'right' });
  doc.text('UNIT PRICE', C.price + 22, y + 5.5, { align: 'right' });
  doc.text('AMOUNT',     PW - M,    y + 5.5, { align: 'right' });
  y += TH_H;

  // ── Rows ─────────────────────────────────────────────────────────────────
  data.items.forEach((r, i) => {
    if (y + ROW_H > PH - 50) {
      doc.addPage();
      y = 10;
      doc.setFillColor(GOLD);
      doc.rect(0, y, PW, TH_H, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7);
      doc.setTextColor(INK);
      doc.text('#',          C.sr + 2,  y + 5.5);
      doc.text('ITEM',       C.item,    y + 5.5);
      doc.text('SKU',        C.sku,     y + 5.5);
      doc.text('QTY',        C.qty + 8, y + 5.5, { align: 'right' });
      doc.text('UNIT PRICE', C.price + 22, y + 5.5, { align: 'right' });
      doc.text('AMOUNT',     PW - M,    y + 5.5, { align: 'right' });
      y += TH_H;
    }

    doc.setFillColor(i % 2 === 0 ? WHITE : ROW_ALT);
    doc.rect(0, y, PW, ROW_H, 'F');

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(INK);
    doc.text(String(i + 1), C.sr + 2, y + 4.8);

    const itemText = doc.getTextWidth(r.itemName) > 68 ? r.itemName.substring(0, 32) + '…' : r.itemName;
    doc.text(itemText, C.item, y + 4.8);

    doc.setFontSize(7);
    doc.setTextColor(GREY_MID);
    doc.text(r.sku || '—', C.sku, y + 4.8);

    doc.setFontSize(7.5);
    doc.setTextColor(INK);
    doc.text(String(r.quantity), C.qty + 8, y + 4.8, { align: 'right' });
    doc.text(inr(r.price),       C.price + 22, y + 4.8, { align: 'right' });

    doc.setFont('helvetica', 'bold');
    doc.text(inr(r.total), PW - M, y + 4.8, { align: 'right' });

    doc.setDrawColor('#E8E0D0');
    doc.setLineWidth(0.2);
    doc.line(M, y + ROW_H, PW - M, y + ROW_H);
    y += ROW_H;
  });

  doc.setDrawColor(GOLD);
  doc.setLineWidth(0.6);
  doc.line(M, y, PW - M, y);
  y += 5;

  // ── Totals ───────────────────────────────────────────────────────────────
  const TX = PW - M - 60;
  const VX = PW - M;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(GREY_DARK);
  doc.text('Subtotal', TX, y); doc.text(inr(data.subtotal), VX, y, { align: 'right' }); y += 6;

  if ((data.tax ?? 0) > 0) {
    doc.text('Tax', TX, y); doc.text(inr(data.tax ?? 0), VX, y, { align: 'right' }); y += 6;
  }
  if (data.discount > 0) {
    doc.text('Discount', TX, y); doc.text(`- ${inr(data.discount)}`, VX, y, { align: 'right' }); y += 6;
  }

  doc.setDrawColor(GOLD);
  doc.setLineWidth(0.5);
  doc.line(TX - 2, y, PW - M, y);
  y += 3;

  doc.setFillColor(GOLD);
  doc.roundedRect(TX - 4, y - 1, PW - M - TX + 4 + M, 9, 1.5, 1.5, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(INK);
  doc.text('TOTAL', TX, y + 5.5);
  doc.text(inr(data.total), VX, y + 5.5, { align: 'right' });
  y += 14;

  if (data.paymentMethod) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(GREY_MID);
    doc.text(`Payment: ${data.paymentMethod.toUpperCase()}`, VX, y, { align: 'right' });
    y += 10;
  }

  // ── Footer ───────────────────────────────────────────────────────────────
  doc.setFillColor(INK);
  doc.rect(0, PH - 14, PW, 14, 'F');
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.setTextColor(GOLD_LIGHT);
  doc.text(
    `Thank you for your business  |  ${data.businessName}  |  Powered by NexaRise`,
    PW / 2, PH - 6,
    { align: 'center' },
  );

  doc.setFillColor(GOLD);
  doc.rect(0, PH - 2, PW, 2, 'F');

  // ── Download ─────────────────────────────────────────────────────────────
  const dateStr = data.date.toLocaleDateString('en-IN', {
    day: '2-digit', month: '2-digit', year: 'numeric',
  }).replace(/\//g, '-');
  const safeName = (data.customerName || 'Invoice')
    .replace(/[^a-zA-Z0-9\s]/g, '')
    .trim()
    .replace(/\s+/g, '_');
  doc.save(`INV_${safeName}_${dateStr}.pdf`);
}
